import { lazy, Suspense, useState, type ReactNode } from 'react';
import { CalendarDays } from 'lucide-react';
import type { Matcher } from 'react-day-picker';
import type { ISODate } from '@contracts/common';
import { addDays, businessDate } from '@domain/dates';
import { buttonVariants } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * The calendar is loaded the first time a picker opens: react-day-picker brings date-fns, which no screen needs
 * until somebody chooses a day.
 */
const Calendar = lazy(() => import('./calendar').then((m) => ({ default: m.Calendar })));
/** The same lazy grid for a popover that draws the calendar inline (S9 "Xin lùi hạn"): one import site, one chunk. */
export const LazyCalendar = Calendar;

/**
 * The app's date picker (2026-09-25): shadcn/ui's pattern, a Radix Popover holding a react-day-picker calendar, since
 * Radix has no date picker of its own. The value is the API's ISO `yyyy-MM-dd` in and out (`''` for none), so a
 * caller keeps the shape a native `type="date"` gave it; `onChange` fires once per whole date chosen, never per
 * keystroke, so a half-typed year can never be sent.
 *
 * The calendar (`calendar.tsx`) is react-day-picker's DOM restyled with utility classes: its own stylesheet is not
 * loaded, and it injects no `<style>` element, so production's `style-src 'self'` has nothing to refuse. Days are the calendar days
 * a person means; "today" is the business day in Ho Chi Minh City (`businessDate`), whatever the laptop's zone.
 */

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** An ISO day as a local midnight, the form react-day-picker reads; `undefined` when it is not a whole date. */
function toDate(iso: string | undefined): Date | undefined {
  const m = iso === undefined ? null : ISO.exec(iso);
  if (m === null) return undefined;
  // `setFullYear`, not the constructor: `new Date(2, …)` would read year 2 as 1902.
  const date = new Date(2000, 0, 1);
  date.setFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(date.getTime()) ? undefined : date;
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0');
/** A local calendar day back to ISO. */
const toIso = (date: Date): string => `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
/** dd/MM/yyyy, the company's reading of a day. */
const shown = (iso: string): string | null => {
  const m = ISO.exec(iso);
  return m === null ? null : `${m[3]}/${m[2]}/${m[1]}`;
};

/**
 * The quick picks under the calendar, from the business day: today, tomorrow, the coming Friday (next week's when
 * today is Friday), the next Monday, and two weeks on.
 */
export function quickDays(today: ISODate): [string, ISODate][] {
  const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
  const friday = ((5 - weekday + 7) % 7) || 7;
  const monday = ((1 - weekday + 7) % 7) || 7;
  return [
    ['Hôm nay', today], ['Ngày mai', addDays(today, 1)], ['Thứ Sáu', addDays(today, friday)],
    ['Tuần sau (T2)', addDays(today, monday)], ['+2 tuần', addDays(today, 14)],
  ];
}

export type DatePickerProps = {
  /** What the day is for; with the value it names the trigger ("Hạn chót: 30/09/2026"). */
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  /** A required day offers no "Xoá". */
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  /** Which edge of the trigger the calendar lines up with; a field at the right of its box takes `end`. */
  align?: 'start' | 'center' | 'end';
  /** Replaces the trigger's own icon and text (a property rail draws its value its own way). */
  children?: ReactNode;
  /** Held open by the caller (the Map's card menu opens it at once); left out, the trigger opens and closes it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function DatePicker({
  label, value, onChange, min, max, required, disabled, placeholder = 'Chọn ngày', id, className, children, align = 'start',
  'aria-describedby': describedBy, 'aria-invalid': invalid, open: held, onOpenChange,
}: DatePickerProps) {
  const [own, setOwn] = useState(false);
  const open = held ?? own;
  const setOpen = (next: boolean) => { setOwn(next); onOpenChange?.(next); };
  const selected = toDate(value);
  const today = businessDate(new Date());
  const todayDate = toDate(today)!;
  const [month, setMonth] = useState<Date>(selected ?? todayDate);
  const minDate = toDate(min);
  const maxDate = toDate(max);
  const off: Matcher[] = [];
  if (minDate !== undefined) off.push({ before: minDate });
  if (maxDate !== undefined) off.push({ after: maxDate });
  const text = shown(value);

  const choose = (next: string) => {
    setOpen(false);
    if (next !== value) onChange(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setMonth(selected ?? todayDate);
        setOpen(next);
      }}
    >
      <PopoverTrigger
        id={id} type="button" disabled={disabled} data-field-trigger=""
        aria-label={`${label}: ${text ?? 'chưa chọn'}`}
        aria-describedby={describedBy} aria-invalid={invalid ? true : undefined}
        className={className ?? cn(
          'inline-flex h-11 pointer-fine:h-9 w-full min-w-0 items-center gap-2 rounded-md border border-[hsl(var(--h-border))] bg-transparent px-3 text-left text-sm shadow-xs outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-invalid:border-destructive',
        )}
      >
        {children ?? (
          <>
            <CalendarDays aria-hidden="true" className="size-4 shrink-0 text-[hsl(var(--h-muted-foreground))]" />
            <span className={cn('truncate', text === null && 'text-[hsl(var(--h-muted-foreground))]')}>{text ?? placeholder}</span>
          </>
        )}
      </PopoverTrigger>
      <PopoverContent
        aria-label="Chọn ngày" role="dialog" align={align} className="w-auto p-3"
        // The calendar puts the focus on its chosen (or today's) day itself.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Suspense fallback={<p className="flex h-[300px] w-[252px] items-center justify-center text-sm text-[hsl(var(--h-muted-foreground))]">Đang tải lịch…</p>}>
          <Calendar
            selected={selected} month={month} onMonthChange={setMonth} today={todayDate}
            startMonth={minDate} endMonth={maxDate} disabled={off}
            onSelect={(date) => choose(toIso(date))}
          />
        </Suspense>
        <div className="mt-2 flex w-[252px] flex-wrap items-center gap-1 border-t border-[hsl(var(--h-border))] pt-2">
          {quickDays(today).map(([word, day]) => {
            const allowed = (min === undefined || day >= min) && (max === undefined || day <= max);
            return (
              <button
                key={word} type="button" disabled={!allowed} title={shown(day) ?? undefined} onClick={() => choose(day)}
                className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'h-8 px-2 pointer-fine:min-h-0 pointer-fine:min-w-0')}
              >
                {word}
              </button>
            );
          })}
          {!required && (
            <button
              type="button" onClick={() => choose('')}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'ml-auto h-8 px-2 pointer-fine:min-h-0 pointer-fine:min-w-0')}
            >
              Xoá
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
